import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/app/Shell";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Eye, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";

function formatRemaining(seconds) {
  if (seconds == null || seconds <= 0) return "0s";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 1) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

export default function Interested() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    try {
      const { data } = await api.get("/interests/received");
      setItems(data.interests);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Tick once a second for the countdown; reload from server every 30s
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    const r = setInterval(load, 30000);
    return () => { clearInterval(t); clearInterval(r); };
  }, []);

  const tapBack = async (it) => {
    try {
      const { data } = await api.post("/interests", { to_user: it.from_user.id, venue_id: it.venue_id });
      if (data.mutual) nav(`/match/${data.match.id}`);
      else load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Shell>
      <p className="font-mono-accent text-[#E60000]">{t("interested.prematch")}</p>
      <h1 className="font-display text-3xl mt-2 tracking-tighter">{t("interested.title")}</h1>
      <p className="text-zinc-500 text-sm mt-1">{t("interested.subtitle")}</p>

      <div className="mt-6 space-y-3" data-testid="interests-list">
        {loading && <p className="text-zinc-500">…</p>}
        {!loading && items.length === 0 && <p className="text-zinc-500 text-sm">{t("interested.nothingYet")}</p>}
        {items.map(it => {
          // expires_at is server time; we approximate remaining locally between server reloads
          let remaining = it.expires_in_seconds || 0;
          if (it.expires_at) {
            const exp = new Date(it.expires_at).getTime();
            remaining = Math.max(0, Math.floor((exp - now) / 1000));
          }
          const ttl = it.ttl_seconds || 900;
          const pct = Math.max(0, Math.min(100, (remaining / ttl) * 100));
          const isUrgent = remaining < 60;
          return (
            <div key={it.interest_id} data-testid={`interest-${it.interest_id}`} className="rounded-2xl border border-white/10 bg-black/60 p-5 text-center relative overflow-hidden">
              {/* Countdown progress bar (top edge) */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5">
                <div
                  className="h-full transition-[width] duration-1000 linear"
                  style={{
                    width: `${pct}%`,
                    background: isUrgent ? "#FF1A1A" : "#E60000",
                    boxShadow: isUrgent ? "0 0 8px rgba(255,26,26,0.7)" : "0 0 6px rgba(230,0,0,0.4)",
                  }}
                />
              </div>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Eye size={26} className="text-[#E60000]" />
              </div>
              <div className="mt-3 font-display text-xl">{it.from_user.real_name || it.from_user.display_name || t("interested.anonymous")}</div>
              <div className="text-xs text-zinc-500 font-mono-accent">{(it.from_user.real_name && it.from_user.display_name) ? it.from_user.display_name : ""}</div>
              <div
                className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono-accent tracking-widest ${isUrgent ? "text-[#FF1A1A]" : "text-zinc-500"}`}
                data-testid={`interest-countdown-${it.interest_id}`}
              >
                <Timer size={11} /> {t("interested.expiresIn",{time:formatRemaining(remaining).toUpperCase()})}
              </div>
              <button
                data-testid={`tapback-${it.interest_id}`}
                onClick={()=>tapBack(it)}
                disabled={remaining <= 0}
                className="imin-btn mt-4 px-8 py-4 font-bold disabled:opacity-30"
              >
                {t("interested.blinkBack")}
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center font-mono-accent text-zinc-600 text-[0.6rem] tracking-wider" data-testid="ttl-footer">
        {t("interested.ttlFooter")}
      </p>
    </Shell>
  );
}
